import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { usePortalAuth } from "@/context/PortalAuthContext";
import { portalApi, NIVELES } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, CheckCircle2 } from "lucide-react";
import { LogoBspark } from "@/components/LogoBspark";
import axios from "axios";

export default function PortalRegistro() {
  const { register } = usePortalAuth();
  const navigate = useNavigate();
  const [sedes, setSedes] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [matricula, setMatricula] = useState(null);
  const [f, setF] = useState({
    nombre: "", apellidos: "", nif: "", telefono: "", email: "", direccion: "", ciudad: "",
    codigo_postal: "", password: "",
    a_nombre: "", a_apellidos: "", a_fecha: "", a_nivel: "naranja", a_notas: "",
    a_sede: "", gdpr: false, gdpr_imagen: false, comunicaciones: false,
  });
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  useEffect(() => {
    const preSede = new URLSearchParams(window.location.search).get("sede");
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/publico/sedes`)
      .then(({ data }) => {
        setSedes(data);
        if (preSede && data.some((s) => s.id === preSede)) set("a_sede", preSede);
      })
      .catch(() => setSedes([]));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!f.a_sede) return setError("Elige la sede en la que quieres inscribir a tu hijo/a.");
    if (!f.gdpr) return setError("Debes aceptar los Términos y Condiciones y la Política de Privacidad.");
    setLoading(true);
    setError("");
    const res = await register({
      padre: {
        nombre: f.nombre, apellidos: f.apellidos, nif: f.nif, telefono: f.telefono,
        email: f.email, direccion: f.direccion, ciudad: f.ciudad, codigo_postal: f.codigo_postal,
        acepta_comunicaciones: f.comunicaciones,
      },
      password: f.password,
      alumno: {
        nombre: f.a_nombre, apellidos: f.a_apellidos, fecha_nacimiento: f.a_fecha, nivel: f.a_nivel,
        observaciones: f.a_notas, sede_id: f.a_sede || null,
        gdpr_aceptado: f.gdpr, gdpr_imagen: f.gdpr_imagen,
      },
    });
    setLoading(false);
    if (res.ok) setMatricula(res.matricula);
    else setError(res.error);
  };

  if (matricula)
    return (
      <div className="min-h-screen grid place-items-center px-6 bg-background">
        <Card className="rounded-2xl p-10 max-w-md w-full text-center fade-up" data-testid="registro-exito">
          <CheckCircle2 className="h-12 w-12 text-primary mx-auto" />
          <h1 className="font-display text-3xl font-extrabold mt-6">¡Inscripción completada!</h1>
          <p className="text-sm text-muted-foreground mt-3">
            Guarda este número de matrícula: lo necesitarás para acceder al área de familias.
          </p>
          <p className="font-display text-4xl font-extrabold text-primary mt-6" data-testid="matricula-generada">
            {matricula}
          </p>
          <Button className="rounded-full font-semibold mt-8 w-full" onClick={() => navigate("/portal")}
            data-testid="ir-al-portal-btn">
            Ir a mi área
          </Button>
        </Card>
      </div>
    );

  return (
    <div className="min-h-screen bg-background px-6 py-14">
      <div className="max-w-3xl mx-auto fade-up">
        <LogoBspark className="h-20 w-20" texto={false} testid="logo-registro" />
        <p className="uppercase tracking-[0.3em] text-xs text-primary font-semibold mb-4 mt-8">FamilyHub</p>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold">Inscripción online</h1>
        <p className="text-muted-foreground text-sm mt-3 mb-10">
          Regístrate como padre/madre/tutor e inscribe a tu hijo/a. Recibirás un número de matrícula automáticamente.
        </p>

        <form onSubmit={submit} className="space-y-6">
          <Card className="rounded-2xl p-6 sm:p-8 border-border shadow-sm">
            <h2 className="font-display text-lg font-bold mb-6">Tus datos (padre / madre / tutor)</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Nombre *</Label>
                <Input required value={f.nombre} onChange={(e) => set("nombre", e.target.value)} data-testid="reg-padre-nombre" />
              </div>
              <div className="grid gap-2">
                <Label>Apellidos</Label>
                <Input value={f.apellidos} onChange={(e) => set("apellidos", e.target.value)} data-testid="reg-padre-apellidos" />
              </div>
              <div className="grid gap-2">
                <Label>NIF / CIF</Label>
                <Input value={f.nif} onChange={(e) => set("nif", e.target.value)} data-testid="reg-padre-nif" />
              </div>
              <div className="grid gap-2">
                <Label>Teléfono</Label>
                <Input value={f.telefono} onChange={(e) => set("telefono", e.target.value)} data-testid="reg-padre-telefono" />
              </div>
              <div className="grid gap-2">
                <Label>Email *</Label>
                <Input type="email" required value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="reg-padre-email" />
              </div>
              <div className="grid gap-2">
                <Label>Contraseña * (mín. 6)</Label>
                <PasswordInput required minLength={6} value={f.password}
                  onChange={(e) => set("password", e.target.value)} data-testid="reg-padre-password" />
              </div>
              <div className="grid gap-2 sm:col-span-2">
                <Label>Dirección</Label>
                <Input value={f.direccion} onChange={(e) => set("direccion", e.target.value)} data-testid="reg-padre-direccion" />
              </div>
              <div className="grid gap-2">
                <Label>Ciudad</Label>
                <Input value={f.ciudad} onChange={(e) => set("ciudad", e.target.value)} data-testid="reg-padre-ciudad" />
              </div>
              <div className="grid gap-2">
                <Label>Código postal</Label>
                <Input value={f.codigo_postal} onChange={(e) => set("codigo_postal", e.target.value)} data-testid="reg-padre-cp" />
              </div>
            </div>
          </Card>

          <Card className="rounded-2xl p-6 sm:p-8 border-border shadow-sm">
            <h2 className="font-display text-lg font-bold mb-6">Datos del alumno/a</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Nombre *</Label>
                <Input required value={f.a_nombre} onChange={(e) => set("a_nombre", e.target.value)} data-testid="reg-alumno-nombre" />
              </div>
              <div className="grid gap-2">
                <Label>Apellidos</Label>
                <Input value={f.a_apellidos} onChange={(e) => set("a_apellidos", e.target.value)} data-testid="reg-alumno-apellidos" />
              </div>
              <div className="grid gap-2">
                <Label>Fecha de nacimiento</Label>
                <Input type="date" value={f.a_fecha} onChange={(e) => set("a_fecha", e.target.value)} data-testid="reg-alumno-fecha" />
              </div>
              <div className="grid gap-2 sm:col-span-2">
                <Label>Sede</Label>
                <Select value={f.a_sede} onValueChange={(v) => set("a_sede", v)}>
                  <SelectTrigger data-testid="reg-alumno-sede"><SelectValue placeholder="Selecciona sede" /></SelectTrigger>
                  <SelectContent>
                    {sedes.map((s) => <SelectItem key={s.id} value={s.id}>{s.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2 sm:col-span-2">
                <Label>Observaciones (opcional)</Label>
                <Textarea value={f.a_notas} onChange={(e) => set("a_notas", e.target.value)}
                  placeholder="Algo que debamos saber" data-testid="reg-alumno-notas" />
              </div>
            </div>
            <div className="rounded-xl border border-border p-5 mt-6 bg-muted/40" data-testid="gdpr-bloque">
              <p className="font-display font-bold mb-3">Protección de datos (GDPR)</p>
              <div className="text-xs text-muted-foreground space-y-2 max-h-44 overflow-y-auto pr-2">
                <p>
                  <b>Responsable:</b> Bspark Art Academy (BSpark hub). <b>Finalidad:</b> gestionar la
                  inscripción y el seguimiento académico del alumno/a, la relación con la familia,
                  el control de asistencia y la facturación de las cuotas.
                </p>
                <p>
                  <b>Base jurídica:</b> ejecución del contrato de servicios formativos y tu
                  consentimiento para los usos opcionales que marques abajo.
                </p>
                <p>
                  <b>Datos de menores:</b> del alumno/a solo tratamos nombre, apellidos, fecha de
                  nacimiento, nivel y las observaciones que nos facilites voluntariamente, con el fin
                  de prestar el servicio formativo.
                </p>
                <p>
                  <b>Destinatarios:</b> la sede en la que se matricula y nuestro proveedor de pagos
                  (Stripe) para gestionar los cobros. No se ceden datos a terceros con fines
                  comerciales.
                </p>
                <p>
                  <b>Conservación:</b> mientras el alumno/a esté matriculado y los plazos legales
                  exigidos después. <b>Derechos:</b> puedes acceder, rectificar, suprimir, limitar u
                  oponerte al tratamiento y solicitar la portabilidad escribiendo a tu sede.
                </p>
              </div>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-border p-3 mt-3">
              <Label>
                He leído y acepto los{" "}
                <a href="/legal/terminos" target="_blank" rel="noreferrer" className="text-primary underline"
                  data-testid="reg-link-terminos">Términos y Condiciones</a>{" "}
                y la{" "}
                <a href="/legal/privacidad" target="_blank" rel="noreferrer" className="text-primary underline"
                  data-testid="reg-link-privacidad">Política de Privacidad</a> *
              </Label>
              <Switch checked={f.gdpr} onCheckedChange={(v) => set("gdpr", v)} data-testid="reg-gdpr-switch" />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-border p-3 mt-3">
              <Label>Autorizo el uso de imágenes de mi hijo/a en la galería de la academia</Label>
              <Switch checked={f.gdpr_imagen} onCheckedChange={(v) => set("gdpr_imagen", v)} data-testid="reg-gdpr-imagen-switch" />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-border p-3 mt-3">
              <Label>Quiero recibir avisos y novedades de la academia por email</Label>
              <Switch checked={f.comunicaciones} onCheckedChange={(v) => set("comunicaciones", v)} data-testid="reg-comunicaciones-switch" />
            </div>
          </Card>

          {error && <p className="text-sm text-destructive font-medium" data-testid="registro-error">{error}</p>}

          <div className="flex items-center gap-4">
            <Button type="submit" disabled={loading} className="rounded-full font-semibold h-11 px-8" data-testid="reg-submit-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Completar inscripción"}
            </Button>
            <Link to="/" className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-200">
              Ya tengo matrícula
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
